import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getGames, deleteGames } from './actions';
import { selectGames, selectGamesLoaded } from './selectors';
import { selectCurrentUser } from '../Login/selectors';
import CreateGame from './CreateGame';
import ConfirmButton from '../ConfirmButton';
import { Button, Muted, Select } from '../Controls';
import { Page, PageTitle, Panel, PanelTitle } from '../Page';
import { gameSummary } from './gameSummary';
import { SORTS, sortGames } from './sortGames';
import { selectRange } from './selectRange';
import MiniBoard from './MiniBoard';
import { Check, GameGrid, GameTile, ListBar, NewMoves, SortBar, Status, TileFooter, TileText } from './styled';
import { shortWhen } from '../dates';
import Avatar from '../Avatar';
import WatchList from '../Watch/WatchList';

const SORT_KEY = 'games-sort';

/** The sort you last picked, on this device; it can be unavailable (a private window), and then it's the default. */
function rememberedSort() {
  try {
    return window.localStorage.getItem(SORT_KEY) || SORTS[0].key;
  } catch (unavailable) {
    return SORTS[0].key;
  }
}

function rememberSort(key) {
  try {
    window.localStorage.setItem(SORT_KEY, key);
  } catch (unavailable) {
    // Not remembered: it's only a convenience.
  }
}

/** Last played, as a date or a time today. */
const Games = () => {
  const dispatch = useDispatch();
  const games = useSelector(selectGames);
  const loaded = useSelector(selectGamesLoaded);
  const currentUser = useSelector(selectCurrentUser);
  const userId = currentUser && currentUser.get('id');
  const [sort, setSort] = useState(rememberedSort);
  // Picking games for something done to all of them at once (deleting, for now): null when not picking.
  const [selected, setSelected] = useState(null);
  const selecting = selected !== null;
  // The last game clicked while picking, where a shift-click's range starts.
  const lastPicked = useRef(null);
  const toggle = (gameId) => setSelected(current => (current.includes(gameId)
    ? current.filter(id => id !== gameId) : [...current, gameId]));

  useEffect(() => {
    dispatch(getGames());
  }, [dispatch]);

  const { going, finished } = sortGames(games, sort, userId);
  const everyId = going.concat(finished).map(game => game.get('id')).toArray();

  const tile = (game) => {
    const summary = gameSummary(game, userId);
    const gameId = game.get('id');
    const picked = selecting && selected.includes(gameId);
    // While picking, a tap picks the game instead of opening it; shift-click picks every game from the last one
    // clicked to this one, and starts picking if it hadn't started.
    const pick = (event) => {
      if (event.shiftKey) {
        event.preventDefault();
        // Read now: the update below runs later, after lastPicked has moved on to this game.
        const from = lastPicked.current;
        setSelected(current => selectRange(everyId, current || [], from, gameId));
      } else if (selecting) {
        event.preventDefault();
        toggle(gameId);
      } else {
        return;
      }
      lastPicked.current = gameId;
    };
    return (
      <GameTile key={gameId} $yourMove={summary.yourMove && !selecting} $idle={!summary.yourMove && !picked} $picked={picked}>
        <Link to={`/games/${gameId}`} onClick={pick} aria-pressed={selecting ? picked : undefined}>
          {selecting && <Check $on={picked} aria-hidden="true">{picked ? '✓' : ''}</Check>}
          <MiniBoard game={game} userId={userId} />
          {summary.newMoves > 0 && <NewMoves data-new-moves>{summary.newMoves} new</NewMoves>}
          <TileText>
            <strong><Avatar user={summary.opponent} size={22} game={gameId} /> {summary.against}</strong>
            <Status $yourMove={summary.yourMove}>{summary.status}</Status>
          </TileText>
        </Link>
        <TileFooter>
          <span>{shortWhen(game.get('updated_at'))}</span>
          {!selecting && <ConfirmButton
            onConfirm={() => dispatch(deleteGames([gameId]))}
            prompt={`Delete the game ${summary.against}?`}
            confirmLabel="Delete"
          >
            Delete
          </ConfirmButton>}
        </TileFooter>
      </GameTile>
    );
  };
  const deleteSelected = () => {
    dispatch(deleteGames(selected));
    setSelected(null);
  };

  return (
    <Page $wide>
      <PageTitle>Games</PageTitle>
      <Panel>
        <PanelTitle>New game</PanelTitle>
        <CreateGame />
      </Panel>
      <Panel>
        <PanelTitle>Your games</PanelTitle>
        {!loaded && <Muted>Loading your games…</Muted>}
        {loaded && !games.size && <Muted>No games yet.</Muted>}
        {games.size > 0 && (
          <ListBar>
            {going.size > 1 && (
              <SortBar>
                Sort by
                <Select value={sort} onChange={(event) => { setSort(event.target.value); rememberSort(event.target.value); }}>
                  {SORTS.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
                </Select>
              </SortBar>
            )}
            {!selecting && <Button onClick={() => { setSelected([]); lastPicked.current = null; }}>Select</Button>}
            {selecting && (
              <>
                <Muted>{selected.length} selected</Muted>
                <Button onClick={() => setSelected(selected.length === everyId.length ? [] : everyId)}>
                  {selected.length === everyId.length ? 'Select none' : 'Select all'}
                </Button>
                <Button onClick={() => setSelected(null)}>Cancel</Button>
                <ConfirmButton
                  onConfirm={deleteSelected}
                  disabled={!selected.length}
                  prompt={`Delete ${selected.length === 1 ? 'this game' : `these ${selected.length} games`}? This can't be undone.`}
                  confirmLabel="Delete"
                >
                  Delete
                </ConfirmButton>
              </>
            )}
          </ListBar>
        )}
        <GameGrid>{going.map(tile)}</GameGrid>
      </Panel>
      {finished.size > 0 && (
        <Panel>
          <PanelTitle>Finished</PanelTitle>
          <GameGrid>{finished.map(tile)}</GameGrid>
        </Panel>
      )}
      <WatchList />
    </Page>
  );
};

export default Games;
